/**
 * Vaperite Category Mappings Tab  (formerly SKC)
 *
 * TODO(vaperite-migration): SKC is being replaced by Vaperite. Display relabel only for now;
 * component name (VaperiteCategoryMappings), file, and the SKU/category mapping model are NOT renamed
 * yet — the full rename + Vaperite catalog taxonomy mapping happen during the Vaperite cutover.
 * See the Vaperite migration spec.
 *
 * Maps the category names found in the Vend sync payload to local Deeliver
 * categories. (These arrived by webhook under SKC; Vend is polled on a timer.)
 * Vend product types (e.g. "Nicotine Shots" > "MTL (15ml)") are the containers
 * that need to map to our product categories for product creation.
 *
 * Note: Webhook "categories" are a different concept — they become product
 * tags and are handled on the content creation page, NOT here.
 *
 * Standalone component imported by VaperiteStagingDashboard.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Paper,
  Skeleton,
  Fade,
  alpha,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  Category as CategoryIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  SelectAll as SelectAllIcon,
  LinkOff as UnmappedIcon,
  Link as MappedIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CreateNewFolder as CreateCategoryIcon,
  Block as ExcludeIcon,
  SettingsBackupRestore as IncludeIcon,
} from '@mui/icons-material';
import ResolveUnmappedCategoryDialog from '../components/ResolveUnmappedCategoryDialog';
import { useNotify } from 'react-admin';
import authInstance from '../services/httpClient';
import { useAdminTheme } from '../admin-theme';
import { useScrollToListTop } from '../hooks/useScrollToListTop';
import { appCategoryOptions, isAppCategory } from './vaperite/appCategories';
import { stagingApiBase } from './vaperite/stagingOrg';

// ============================================================================
// Types
// ============================================================================

interface PayloadCategory {
  id: number;
  name: string;
  firstSeen: string;
  lastSeen: string;
  /** Per-payload sync counter; the column shows productCount. */
  itemCount: number;
  /** Distinct SKUs staged under this type. */
  productCount: number;
  sourceStores: string[];
  /** Marked New & Popular: approving an item in this category flags it Popular. */
  flagsPopular: boolean;
  /** New items held in staging because this category is unmapped (Processing Rules: Stage). */
  waitingCount?: number;
  /** Full Vend type path, root first (e.g. Nicotine Shots > MTL (15ml)). */
  path?: string[];
  /** True when other Vend types sit under this one. */
  isParent?: boolean;
  /** Excluded: its pending new items are withdrawn and new ones arrive withdrawn at sync. */
  excluded?: boolean;
  /** Excluded with "hide its live products": the type's live products are hidden from the app. */
  hideLive?: boolean;
  /** Live products of this type (Sell in app ON, active). */
  liveCount?: number;
  mappedTo: {
    localCategoryId: string;
    localCategoryName: string;
    priority: number;
  } | null;
}

interface LocalCategory {
  id: string;
  name: string;
  legacyExternalId: string | null;
  isActive: boolean;
  homeFilterType: string | null;
}

/**
 * Categories a product can actually live in — what the default fallback may be.
 * Excludes switched-off categories and home tiles that filter by tag/brand rather
 * than holding products. New & Popular IS allowed: products placed there are also
 * flagged Popular, so they show in the home row.
 */
const isAssignableCategory = (lc: LocalCategory) =>
  lc.isActive && !['tag', 'brand'].includes(lc.homeFilterType ?? '');

interface MappingStats {
  total: number;
  mapped: number;
  unmapped: number;
  /** Excluded types — not counted as unmapped. */
  excluded?: number;
  defaultCategoryId: string;
  defaultCategoryName: string | null;
}

// ============================================================================
// Glow Summary Card (duplicated lightweight version — avoids circular import)
// ============================================================================

function GlowCard({ icon, label, value, color, subtitle }: {
  icon: React.ReactElement;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
}) {
  const { colors } = useAdminTheme();
  return (
    <Paper
      sx={{
        p: 2.5,
        height: '100%',
        bgcolor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '&:hover': {
          bgcolor: colors.bgElevated,
          borderColor: alpha(color, 0.4),
          boxShadow: `0 0 30px ${alpha(color, 0.15)}, inset 0 0 30px ${alpha(color, 0.05)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.1), color,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.08em' }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 800, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function VaperiteCategoryMappings() {
  const { colors, elevatedInputStyle } = useAdminTheme();
  const notify = useNotify();

  // Data state
  const [payloadCategories, setPayloadCategories] = useState<PayloadCategory[]>([]);
  const [localCategories, setLocalCategories] = useState<LocalCategory[]>([]);
  const [stats, setStats] = useState<MappingStats | null>(null);
  /** True when the table is showing the agreed mapping rather than real payloads. */
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // When the Vend sync last ran. These category names are the leaves of that
  // payload, so the sync time is what says whether this page is showing current
  // data — nothing arrives by webhook any more.
  const [vendSync, setVendSync] = useState<{ lastRunAt: string | null; vendOrg?: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authInstance.get('admin/commands/vend-sync-settings');
        setVendSync(data?.settings ?? null);
      } catch {
        // Non-fatal — the card falls back to "Never".
      }
    })();
  }, []);

  // Filter state
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'mapped' | 'unmapped' | 'excluded'>('all');
  const [page, setPage] = useState(1);
  const listTopRef = useScrollToListTop(page);
  const limit = 50;

  // Inline edit state
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editPriority, setEditPriority] = useState(0);
  const [saving, setSaving] = useState(false);
  const [categorySelectOpen, setCategorySelectOpen] = useState(false);

  // Selection for bulk assign
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [defaultCategoryId, setDefaultCategoryId] = useState('');
  // "Create category" (or map) for one unmapped Vend category.
  const [resolveCat, setResolveCat] = useState<PayloadCategory | null>(null);
  // Exclude on a MAPPED type (removes its mapping) or one with live products: confirm first.
  const [confirmExclude, setConfirmExclude] = useState<PayloadCategory | null>(null);
  // In that dialog: also hide the type's live products from the app (off by default).
  const [hideLive, setHideLive] = useState(false);

  // ------ Data fetching ------

  const fetchPayloadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit, mapped: filterMode };
      if (search) params.search = search;
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/category-mappings`, { params });
      setPayloadCategories(data.categories || []);
      setTotal(data.total || 0);
      if (data.stats) setStats(data.stats);
    } catch (err: any) {
      notify(`Failed to load category mappings: ${err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, filterMode, search, notify]);

  const fetchLocalCategories = useCallback(async () => {
    try {
      const { data } = await authInstance.get(`${stagingApiBase()}/staging/category-mappings/local`);
      setLocalCategories(data.categories || []);
    } catch {
      // Will retry on next load
    }
  }, []);

  useEffect(() => {
    fetchLocalCategories();
  }, [fetchLocalCategories]);

  useEffect(() => {
    fetchPayloadCategories();
  }, [fetchPayloadCategories]);

  // ------ Actions ------

  const handleSaveMapping = async (payloadCategoryName: string) => {
    if (!editCategoryId) return;
    setSaving(true);
    try {
      await authInstance.post(`${stagingApiBase()}/staging/category-mappings`, {
        payloadCategoryName,
        localCategoryId: editCategoryId,
        priority: editPriority,
      });
      notify('Mapping saved', { type: 'success' });
      setEditingRow(null);
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Failed to save mapping: ${err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // New & Popular is a flag, not a destination: marking a Vaperite category flags its
  // items Popular on approval; their real category still comes from the mapping/default.
  const handleTogglePopular = async (cat: PayloadCategory) => {
    try {
      await authInstance.post(`${stagingApiBase()}/staging/category-mappings/popular`, {
        payloadCategoryName: cat.name,
        on: !cat.flagsPopular,
      });
      notify(
        cat.flagsPopular
          ? `"${cat.name}" no longer flags New & Popular`
          : `"${cat.name}" now flags its items New & Popular on approval`,
        { type: 'success' },
      );
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Failed to update New & Popular: ${err.message}`, { type: 'error' });
    }
  };

  // Excluded: the type's pending new items are withdrawn and new ones are dropped at
  // sync; Include brings back exactly what excluding withdrew.
  const handleSetExcluded = async (cat: PayloadCategory, excluded: boolean, hideLiveProducts = false) => {
    try {
      const { data } = await authInstance.put(
        `${stagingApiBase()}/staging/category-mappings/${encodeURIComponent(cat.name)}/excluded`,
        excluded ? { excluded, hideLive: hideLiveProducts } : { excluded },
      );
      notify(
        excluded
          ? `"${cat.name}" excluded — ${data?.withdrawn ?? 0} pending item(s) withdrawn${hideLiveProducts ? `, ${data?.hidden ?? 0} live product(s) hidden` : ''}`
          : `"${cat.name}" included — ${data?.restored ?? 0} item(s) back in review${data?.unhidden ? `, ${data.unhidden} live product(s) shown again` : ''}`,
        { type: 'success' },
      );
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Failed to update "${cat.name}": ${err.message}`, { type: 'error' });
    }
  };

  const handleDeleteMapping = async (name: string) => {
    try {
      await authInstance.delete(`${stagingApiBase()}/staging/category-mappings/${encodeURIComponent(name)}`);
      notify('Mapping deleted', { type: 'success' });
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Failed to delete mapping: ${err.message}`, { type: 'error' });
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkCategoryId || selected.size === 0) return;
    try {
      await authInstance.post(`${stagingApiBase()}/staging/category-mappings/bulk`, {
        payloadCategoryNames: Array.from(selected),
        localCategoryId: bulkCategoryId,
      });
      notify(`${selected.size} category mappings saved`, { type: 'success' });
      setSelected(new Set());
      setBulkDialogOpen(false);
      setBulkCategoryId('');
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Bulk assign failed: ${err.message}`, { type: 'error' });
    }
  };

  const [applying, setApplying] = useState(false);
  const handleApplyNow = async () => {
    setApplying(true);
    try {
      const { data } = await authInstance.post(`${stagingApiBase()}/staging/category-mappings/apply`);
      const plural = (n: number) => `${n} item${n === 1 ? '' : 's'}`;
      notify(
        data.withdrawn || data.restored
          ? `Applied: ${plural(data.withdrawn)} moved to Excluded Products, ${plural(data.restored)} back to review`
          : 'Applied: everything already matches the mappings',
        { type: 'success' },
      );
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Apply failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const handleSetDefault = async () => {
    try {
      await authInstance.put(`${stagingApiBase()}/staging/category-mappings/default`, {
        categoryId: defaultCategoryId,
      });
      notify('Default category updated', { type: 'success' });
      setDefaultDialogOpen(false);
      fetchPayloadCategories();
    } catch (err: any) {
      notify(`Failed to set default: ${err.message}`, { type: 'error' });
    }
  };

  const toggleSelect = (name: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === payloadCategories.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(payloadCategories.map(c => c.name)));
    }
  };

  const startEdit = (cat: PayloadCategory) => {
    setCategorySelectOpen(false);
    setEditingRow(cat.name);
    setEditCategoryId(cat.mappedTo?.localCategoryId || '');
    setEditPriority(cat.mappedTo?.priority ?? 0);
  };

  const cancelEdit = () => {
    if (categorySelectOpen) return; // dropdown is open — don't dismiss
    setEditingRow(null);
    setCategorySelectOpen(false);
  };

  const formatRelativeTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // ------ Render ------


  const lastSyncLabel = vendSync?.lastRunAt
    ? new Date(vendSync.lastRunAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })
    : 'Never';
  const lastSyncSubtitle = vendSync?.lastRunAt
    ? `${stats?.total ?? 0} categories from the ${vendSync.vendOrg ?? 'Vend'} catalogue`
    : 'No sync has run yet';

  return (
    <Fade in timeout={300}>
      <Box>
        {/* Stats Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            {/* Was "Webhook / Discovered from webhooks". Nothing arrives by
                webhook now — the Vend catalogue is POLLED on a timer. When that
                last ran is the fact that tells you whether this page is current. */}
            <GlowCard
              label="Last Vend Sync"
              value={lastSyncLabel}
              color={colors.info}
              icon={<CategoryIcon />}
              subtitle={lastSyncSubtitle}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Mapped"
              value={stats?.mapped ?? 0}
              color={colors.success}
              icon={<MappedIcon />}
              subtitle="Linked to catalogue categories"
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Unmapped"
              value={stats?.unmapped ?? 0}
              color={colors.warning}
              icon={<UnmappedIcon />}
              subtitle={stats?.excluded ? `Need mapping assignment · ${stats.excluded} excluded` : 'Need mapping assignment'}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard
              label="Default Category"
              value={stats?.defaultCategoryName || 'None'}
              color={colors.purple}
              icon={<SettingsIcon />}
              subtitle="Fallback for unmapped items"
            />
          </Grid>
        </Grid>

        {/* Toolbar */}
        <Paper ref={listTopRef} sx={{
          p: 2,
          mb: 2.5,
          bgcolor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}>
          <TextField
            size="small"
            placeholder="Search product types..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            sx={{ ...elevatedInputStyle, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 140, ...elevatedInputStyle }}>
            <InputLabel sx={{ color: colors.textMuted }}>Filter</InputLabel>
            <Select
              value={filterMode}
              label="Filter"
              onChange={(e) => { setFilterMode(e.target.value as any); setPage(1); }}
              sx={{ color: colors.textPrimary }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="mapped">Mapped</MenuItem>
              <MenuItem value="unmapped">Unmapped</MenuItem>
              <MenuItem value="excluded">Excluded</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            startIcon={<SelectAllIcon />}
            disabled={selected.size === 0}
            onClick={() => setBulkDialogOpen(true)}
            sx={{
              color: colors.info,
              borderColor: alpha(colors.info, 0.3),
              '&:hover': { borderColor: colors.info, bgcolor: alpha(colors.info, 0.08) },
            }}
          >
            Bulk Assign ({selected.size})
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => {
              setDefaultCategoryId(stats?.defaultCategoryId || '');
              setDefaultDialogOpen(true);
            }}
            sx={{
              color: colors.purple,
              borderColor: alpha(colors.purple, 0.3),
              '&:hover': { borderColor: colors.purple, bgcolor: alpha(colors.purple, 0.08) },
            }}
          >
            Set Default
          </Button>
        </Paper>

        {/* Main Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.info }} />
          </Box>
        ) : payloadCategories.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              bgcolor: alpha(colors.info, 0.08),
              color: colors.info,
              border: `1px solid ${alpha(colors.info, 0.2)}`,
            }}
          >
            No categories found. They are discovered from the Vend catalogue on each sync.
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ color: colors.textMuted, fontWeight: 600, mb: 1 }}>
              Vend product type → Deeliver App Category
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { color: colors.textMuted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${colors.border}` } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.size === payloadCategories.length && payloadCategories.length > 0}
                        indeterminate={selected.size > 0 && selected.size < payloadCategories.length}
                        onChange={toggleSelectAll}
                        sx={{ color: colors.textMuted, '&.Mui-checked': { color: colors.info } }}
                      />
                    </TableCell>
                    <TableCell>Vend product type</TableCell>
                    <TableCell align="center">Items</TableCell>
                    <TableCell align="center">Stores</TableCell>
                    <TableCell>Last Seen</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Deeliver App Category</TableCell>
                    <TableCell align="center">Priority</TableCell>
                    <TableCell align="center">New &amp; Popular</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payloadCategories.map((cat) => {
                    const isEditing = editingRow === cat.name;

                    const excludeButton = (
                      <Tooltip title="Exclude: withdraw its pending new items; new ones arrive withdrawn">
                        <IconButton
                          size="small"
                          aria-label={`Exclude ${cat.name}`}
                          onClick={() => {
                            if (cat.mappedTo || (cat.liveCount ?? 0) > 0) {
                              setHideLive(false);
                              setConfirmExclude(cat);
                            } else {
                              handleSetExcluded(cat, true);
                            }
                          }}
                          sx={{ color: colors.textMuted, '&:hover': { bgcolor: alpha(colors.textMuted, 0.1) } }}
                        >
                          <ExcludeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    );

                    const row = (
                      <TableRow
                        data-excluded={cat.excluded ? 'true' : undefined}
                        sx={{
                          ...(cat.excluded && !isEditing ? { opacity: 0.55 } : {}),
                          '&:hover': { bgcolor: alpha(colors.info, 0.03) },
                          '& td': { color: colors.textSecondary, borderBottom: `1px solid ${alpha(colors.border, 0.5)}`, fontSize: '0.8rem' },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.has(cat.name)}
                            onChange={() => toggleSelect(cat.name)}
                            sx={{ color: colors.textMuted, '&.Mui-checked': { color: colors.info } }}
                          />
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.8rem' }}>
                              {(cat.path?.length ? cat.path : [cat.name]).join(' > ')}
                            </Typography>
                            {cat.isParent && (
                              <Chip label="parent" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(colors.purple, 0.1), color: colors.purple, fontWeight: 600 }} />
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            label={cat.productCount}
                            size="small"
                            sx={{
                              bgcolor: alpha(colors.info, 0.1),
                              color: colors.info,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title={cat.sourceStores?.join(', ') || 'None'}>
                            <Chip
                              label={cat.sourceStores?.length ?? 0}
                              size="small"
                              sx={{
                                bgcolor: alpha(colors.purple, 0.1),
                                color: colors.purple,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                            {formatRelativeTime(cat.lastSeen)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {isEditing ? (
                            <FormControl size="small" fullWidth sx={elevatedInputStyle}>
                              <Select
                                value={editCategoryId}
                                onChange={(e) => setEditCategoryId(e.target.value)}
                                onOpen={() => setCategorySelectOpen(true)}
                                onClose={() => setCategorySelectOpen(false)}
                                displayEmpty
                                sx={{ color: colors.textPrimary, fontSize: '0.8rem' }}
                              >
                                <MenuItem value="">
                                  <em>-- Select category --</em>
                                </MenuItem>
                                {appCategoryOptions(localCategories, cat.mappedTo?.localCategoryId).map((lc) => (
                                  <MenuItem key={lc.id} value={lc.id}>
                                    {lc.name}
                                    {isAppCategory(lc) ? '' : lc.isActive ? ' (home tile)' : ' (switched off)'}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : cat.excluded ? (
                            <Chip
                              label={cat.hideLive ? 'Excluded · live hidden' : 'Excluded'}
                              title={cat.hideLive
                                ? 'New items arrive withdrawn and its live products are hidden from the app; Include brings both back'
                                : 'New items of this type arrive withdrawn; Include brings them back for review'}
                              size="small"
                              sx={{
                                bgcolor: alpha(colors.textMuted, 0.12),
                                color: colors.textMuted,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          ) : cat.mappedTo ? (
                            <Chip
                              label={cat.mappedTo.localCategoryName}
                              size="small"
                              onClick={() => startEdit(cat)}
                              sx={{
                                bgcolor: alpha(colors.success, 0.1),
                                color: colors.success,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(colors.success, 0.2) },
                              }}
                            />
                          ) : (
                            <Chip
                              label={cat.waitingCount ? `Unmapped · ${cat.waitingCount} waiting` : 'Unmapped'}
                              title={cat.waitingCount ? `${cat.waitingCount} new item(s) held in staging until this is mapped` : undefined}
                              size="small"
                              onClick={() => startEdit(cat)}
                              sx={{
                                bgcolor: alpha(colors.warning, 0.1),
                                color: colors.warning,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                border: `1px dashed ${alpha(colors.warning, 0.3)}`,
                                '&:hover': { bgcolor: alpha(colors.warning, 0.2) },
                              }}
                            />
                          )}
                        </TableCell>

                        <TableCell align="center">
                          {isEditing ? (
                            <TextField
                              size="small"
                              type="number"
                              value={editPriority}
                              onChange={(e) => setEditPriority(parseInt(e.target.value, 10) || 0)}
                              sx={{ ...elevatedInputStyle, width: 60 }}
                              inputProps={{ min: 0, max: 100, style: { textAlign: 'center', fontSize: '0.8rem' } }}
                            />
                          ) : cat.mappedTo ? (
                            <Typography variant="body2" sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                              {cat.mappedTo.priority}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title={cat.flagsPopular
                            ? 'Items in this category are flagged New & Popular — click to stop'
                            : 'Flag items in this category New & Popular on approval'}>
                            <IconButton
                              size="small"
                              aria-label={`Toggle New & Popular for ${cat.name}`}
                              onClick={() => handleTogglePopular(cat)}
                              sx={{ color: cat.flagsPopular ? colors.warning : colors.textMuted }}
                            >
                              {cat.flagsPopular ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="center">
                          {isEditing ? (
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Save">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveMapping(cat.name)}
                                    disabled={!editCategoryId || saving}
                                    sx={{ color: colors.success, '&:hover': { bgcolor: alpha(colors.success, 0.1) } }}
                                  >
                                    {saving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={() => { setCategorySelectOpen(false); setEditingRow(null); }}
                                  sx={{ color: colors.textMuted, '&:hover': { bgcolor: alpha(colors.textMuted, 0.1) } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : cat.excluded ? (
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Include: bring back its withdrawn items for review">
                                <IconButton
                                  size="small"
                                  aria-label={`Include ${cat.name}`}
                                  onClick={() => handleSetExcluded(cat, false)}
                                  sx={{ color: colors.info, '&:hover': { bgcolor: alpha(colors.info, 0.1) } }}
                                >
                                  <IncludeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : cat.mappedTo ? (
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Edit mapping">
                                <IconButton
                                  size="small"
                                  onClick={() => startEdit(cat)}
                                  sx={{ color: colors.info, '&:hover': { bgcolor: alpha(colors.info, 0.1) } }}
                                >
                                  <CategoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete mapping">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteMapping(cat.name)}
                                  sx={{ color: colors.error, '&:hover': { bgcolor: alpha(colors.error, 0.1) } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {excludeButton}
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Map this category">
                                <IconButton
                                  size="small"
                                  onClick={() => startEdit(cat)}
                                  sx={{ color: colors.warning, '&:hover': { bgcolor: alpha(colors.warning, 0.1) } }}
                                >
                                  <CategoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Create a Deeliver App Category for this and map it">
                                <IconButton
                                  size="small"
                                  onClick={() => setResolveCat(cat)}
                                  sx={{ color: colors.success, '&:hover': { bgcolor: alpha(colors.success, 0.1) } }}
                                >
                                  <CreateCategoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {excludeButton}
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );

                    return isEditing ? (
                      <ClickAwayListener key={cat.id} onClickAway={cancelEdit} mouseEvent="onMouseDown">
                        {row}
                      </ClickAwayListener>
                    ) : row;
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {total > limit && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(total / limit)}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: colors.textSecondary,
                      '&.Mui-selected': {
                        bgcolor: alpha(colors.info, 0.15),
                        color: colors.info,
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Apply now (Jason, 28 Oct 2026): staged items follow the saved mappings at once */}
        <Paper sx={{ mt: 2, p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, flex: 1, minWidth: 240 }}>
            Saving a mapping applies it straight away. Apply now runs every saved mapping again: Excluded types&apos; new items
            move to Excluded Products, and items of types no longer Excluded come back to review.
          </Typography>
          <Button variant="contained" size="small" onClick={handleApplyNow} disabled={applying} sx={{ textTransform: 'none' }}>
            {applying ? 'Applying…' : 'Apply now'}
          </Button>
        </Paper>

        {/* ============================================================ */}
        {/* Bulk Assign Dialog */}
        {/* ============================================================ */}
        <Dialog
          open={bulkDialogOpen}
          onClose={() => setBulkDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
              boxShadow: `0 0 40px ${alpha(colors.info, 0.15)}`,
            },
          }}
        >
          <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SelectAllIcon sx={{ color: colors.info }} />
              <span>Bulk Assign Categories</span>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Map {selected.size} selected {selected.size === 1 ? 'Vend category' : 'Vend categories'} to a Deeliver App Category.
            </Typography>
            <Box sx={{ mb: 2 }}>
              {Array.from(selected).map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  onDelete={() => toggleSelect(name)}
                  sx={{
                    m: 0.5,
                    bgcolor: alpha(colors.info, 0.1),
                    color: colors.info,
                    fontSize: '0.75rem',
                    '& .MuiChip-deleteIcon': { color: alpha(colors.info, 0.5) },
                  }}
                />
              ))}
            </Box>
            <FormControl fullWidth sx={elevatedInputStyle}>
              <InputLabel sx={{ color: colors.textMuted }}>Deeliver App Category</InputLabel>
              <Select
                value={bulkCategoryId}
                label="Deeliver App Category"
                onChange={(e) => setBulkCategoryId(e.target.value)}
                sx={{ color: colors.textPrimary }}
              >
                {appCategoryOptions(localCategories).map((lc) => (
                  <MenuItem key={lc.id} value={lc.id}>
                    {lc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
            <Button onClick={() => setBulkDialogOpen(false)} sx={{ color: colors.textSecondary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkAssign}
              disabled={!bulkCategoryId}
              sx={{
                bgcolor: colors.info,
                color: '#000',
                fontWeight: 700,
                '&:hover': { bgcolor: colors.info, boxShadow: `0 0 20px ${alpha(colors.info, 0.4)}` },
              }}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============================================================ */}
        {/* Set Default Category Dialog */}
        {/* ============================================================ */}
        <Dialog
          open={defaultDialogOpen}
          onClose={() => setDefaultDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
              boxShadow: `0 0 40px ${alpha(colors.purple, 0.15)}`,
            },
          }}
        >
          <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SettingsIcon sx={{ color: colors.purple }} />
              <span>Set Default Category</span>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              When a Vend category has no mapping, items are assigned to this default category.
              Leave empty to fall back to "General".
            </Typography>
            <FormControl fullWidth sx={elevatedInputStyle}>
              <InputLabel sx={{ color: colors.textMuted }}>Default Category</InputLabel>
              <Select
                value={defaultCategoryId}
                label="Default Category"
                onChange={(e) => setDefaultCategoryId(e.target.value)}
                sx={{ color: colors.textPrimary }}
              >
                <MenuItem value="">
                  <em>None (fall back to "General")</em>
                </MenuItem>
                {localCategories.filter(isAssignableCategory).map((lc) => (
                  <MenuItem key={lc.id} value={lc.id}>
                    {lc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
            <Button onClick={() => setDefaultDialogOpen(false)} sx={{ color: colors.textSecondary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSetDefault}
              sx={{
                bgcolor: colors.purple,
                color: '#fff',
                fontWeight: 700,
                '&:hover': { bgcolor: colors.purple, boxShadow: `0 0 20px ${alpha(colors.purple, 0.4)}` },
              }}
            >
              Save Default
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============================================================ */}
        {/* Confirm Exclude (mapped type, or one with live products) */}
        {/* ============================================================ */}
        <Dialog
          open={confirmExclude !== null}
          onClose={() => setConfirmExclude(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ExcludeIcon sx={{ color: colors.warning }} />
              <span>Exclude &ldquo;{confirmExclude?.name}&rdquo;?</span>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              {confirmExclude?.mappedTo && (
                <>This type is mapped to <strong>{confirmExclude.mappedTo.localCategoryName}</strong>. Excluding it removes that mapping, </>
              )}
              {confirmExclude?.mappedTo ? 'withdraws' : 'Excluding it withdraws'} its pending new items, and new ones
              arrive withdrawn. Including it later brings the withdrawn items back
              {confirmExclude?.mappedTo ? ', but the type will need mapping again.' : '.'}
            </Typography>
            {(confirmExclude?.liveCount ?? 0) > 0 && (
              <>
                <FormControlLabel
                  sx={{ mt: 2, alignItems: 'flex-start' }}
                  control={(
                    <Checkbox
                      checked={hideLive}
                      onChange={(e) => setHideLive(e.target.checked)}
                      sx={{ pt: 0.25 }}
                    />
                  )}
                  label={(
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                      Also hide its {confirmExclude?.liveCount} live product{confirmExclude?.liveCount === 1 ? '' : 's'} from the app
                    </Typography>
                  )}
                />
                <Typography variant="caption" component="p" sx={{ color: colors.textMuted, ml: 4 }}>
                  {hideLive
                    ? 'They stay in Vend and keep syncing, just not shown in the app. Including the type shows them again.'
                    : 'Left unticked, products that already exist stay in the app and keep syncing.'}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${colors.border}` }}>
            <Button onClick={() => setConfirmExclude(null)} sx={{ color: colors.textSecondary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const cat = confirmExclude;
                setConfirmExclude(null);
                if (cat) handleSetExcluded(cat, true, hideLive);
              }}
              sx={{ bgcolor: colors.warning, color: '#000', fontWeight: 700, '&:hover': { bgcolor: colors.warning } }}
            >
              Exclude
            </Button>
          </DialogActions>
        </Dialog>

        <ResolveUnmappedCategoryDialog
          vendCategory={resolveCat?.name ?? null}
          waitingCount={resolveCat?.waitingCount}
          initialMode="create"
          onClose={() => setResolveCat(null)}
          onResolved={() => { fetchPayloadCategories(); fetchLocalCategories(); }}
        />
      </Box>
    </Fade>
  );
}
